import { ampli } from '@/ampli';
import { useConnectWallet, usePrivy } from '@privy-io/react-auth';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';

import { SelectTokenDialog } from '@/components/SelectTokenDialog';
import { resetSelector, useTradeModuleStore } from '@/components/TransactionModule/store';

import { useFeature } from '@/hooks/useFeature';
import { useKeyPress } from '@/hooks/useKeypress';
import { useSearchShortcut } from '@/hooks/useSearchShortcut';
import { useTokenRoute } from '@/hooks/useTokenRoute';

import { recentTransactionSelector, useTransactionStore } from '@/store/transaction';

import { NotificationIndicatorIcon } from '@/ui/Icons/NotificationIndicatorIcon';
import { SearchIcon } from '@/ui/Icons/SearchIcon';

import { TokenBase } from '@/utils/0x/token-registry.types';
import { HOMEPAGE_REVAMP } from '@/utils/featureFlags';
import { getDefaultTradeParams } from '@/utils/swap';

import { useMatchaWallets } from '../../hooks/useMatchaWallets';
import { buttons } from '../../styles/buttons';
import { fontStyles } from '../../styles/typography';
import { SEARCH_TYPES } from '../../utils/amplitude';
// utils
import { routes } from '../../utils/routes';
import { SearchShortcutKeycap } from '../SearchShortcutKeycap';
import { AdditionalOptionsDropdown } from './AdditionalOptionsDropdown';
import { FeedbackTooltip } from './FeedbackTooltip';
import { HeaderBase } from './HeaderBase';
import { FeatureFlagIndicator, HeaderConnectButton } from './HeaderButtons';
import { MatchaLogoButton } from './MatchaLogoButton';

/**
 * Props for the Header component
 */
interface HeaderProps {
  /** Whether to show or hide the global search bar. */
  hideSearch?: boolean;
  isHomepage?: boolean;
}

/**
 * Landing page hero content.
 * @param props - HeaderProps
 */
export const Header = ({ hideSearch = false, isHomepage = false }: HeaderProps) => {
  const router = useRouter();
  const hasSlashBeenPressed = useKeyPress('/');
  const resetCheckout = useTradeModuleStore(resetSelector);
  const { recentTransaction } = useTransactionStore(recentTransactionSelector);

  const handleConnectionSuccess = () => {
    const target = redirectOnConnectionSuccess.current;
    if (target) {
      redirectOnConnectionSuccess.current = null;
      router.push(target);
    }
  };

  const { isConnected, connect } = useMatchaWallets();
  const homepageRevamp = useFeature(HOMEPAGE_REVAMP);
  const tokenRoute = useTokenRoute();
  // Register a separate onSuccess for the redirect side effect; Privy fans out
  // useConnectWallet callbacks across all consumers that register one.
  useConnectWallet({ onSuccess: handleConnectionSuccess });
  const { isModalOpen, ready } = usePrivy();

  const [showFeedbackHint, setShowFeedbackHint] = useState(false);
  const [mounted, setMounted] = useState(false);

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const redirectOnConnectionSuccess = useRef<
    typeof routes.TRADE_HISTORY | typeof routes.PORTFOLIO | null
  >(null);

  // Listen for feedback hint changes from other components
  useEffect(() => {
    const handleFeedbackHintChange = (event: CustomEvent<boolean>) => {
      const shouldShow = event.detail && isConnected;
      setShowFeedbackHint(shouldShow);
    };

    window.addEventListener('feedbackHintChanged', handleFeedbackHintChange as EventListener);
    return () => {
      window.removeEventListener('feedbackHintChanged', handleFeedbackHintChange as EventListener);
    };
  }, [isConnected]);

  useEffect(() => {
    if (!hasSlashBeenPressed) return;
    const element = document.activeElement;

    const shouldFocus =
      !(element instanceof HTMLInputElement) &&
      !(element instanceof HTMLTextAreaElement) &&
      !(
        element instanceof HTMLElement &&
        (element.contentEditable === 'true' || element.contentEditable === '')
      );
    if (!shouldFocus) return;

    // set timeout so focus happens after the slash key event has bubbled
    // so '/' isn't typed into the input field.
    setTimeout(() => buttonRef.current?.click(), 0);
  }, [hasSlashBeenPressed]);

  // Ctrl+K / ⌘K opens search from anywhere, including while an input is
  // focused (command-palette convention).
  useSearchShortcut(() => buttonRef.current?.click());

  const handleHistoryClick = (event: React.MouseEvent) => {
    ampli.navHistoryClicked();

    if (!ready) return;
    if (!isConnected) {
      event.preventDefault();
      connect();
      redirectOnConnectionSuccess.current = routes.TRADE_HISTORY;
    }
  };

  const handlePortfolioClick = (event: React.MouseEvent) => {
    if (!ready) return;
    if (!isConnected) {
      event.preventDefault();
      connect();
      redirectOnConnectionSuccess.current = routes.PORTFOLIO;
    }
  };

  const handleSelectTokenResult = async (token: TokenBase) => {
    await router.push(tokenRoute({ address: token.address, chainId: token.chainId }));
    resetCheckout();
  };

  useEffect(() => setMounted(true), []);

  // Do not redirect on wallet connection success when user does not complete
  // the wallet connection after clicking on the history link
  useEffect(() => {
    if (!isModalOpen && !isConnected && redirectOnConnectionSuccess.current) {
      redirectOnConnectionSuccess.current = null;
    }
  }, [isModalOpen, isConnected]);

  // event tracking
  const searchType = isHomepage ? SEARCH_TYPES.HOME_PAGE : SEARCH_TYPES.HEADER;

  const tradePageQuery = getDefaultTradeParams();
  const tradePage = { pathname: routes.SWAP, query: tradePageQuery };

  return (
    <HeaderBase data-hide-search={hideSearch} data-connected={isConnected}>
      <nav className="flex items-center gap-6 grow">
        <MatchaLogoButton />
        <div className="flex-row hidden header-sm:flex">
          {homepageRevamp ? (
            <Link
              href={routes.TOKENS}
              className={buttons({ size: 'small', shape: 'pill', color: 'transparent' })}
              onClick={() => ampli.navTokensClicked()}
            >
              Tokens
            </Link>
          ) : (
            <>
              <Link
                href={routes.INDEX}
                className={buttons({ size: 'small', shape: 'pill', color: 'transparent' })}
                onClick={() => ampli.navDiscoverClicked()}
              >
                Discover
              </Link>
              <Link
                href={tradePage}
                className={buttons({ size: 'small', shape: 'pill', color: 'transparent' })}
                onClick={() => ampli.navTradeClicked()}
              >
                Trade
              </Link>
            </>
          )}
          <Link
            href={routes.PORTFOLIO}
            className={buttons({ size: 'small', shape: 'pill', color: 'transparent' })}
            onClick={handlePortfolioClick}
          >
            Portfolio
          </Link>
          <Link
            href={routes.TRADE_HISTORY}
            className={buttons({ size: 'small', shape: 'pill', color: 'transparent' })}
            onClick={handleHistoryClick}
          >
            History
            {mounted && recentTransaction && isConnected && (
              <NotificationIndicatorIcon className="ml-1.5" />
            )}
          </Link>
        </div>
      </nav>
      <div className="flex flex-row items-center justify-end grow gap-2">
        <SelectTokenDialog onSelect={handleSelectTokenResult} searchType={searchType}>
          <button
            className={fontStyles({
              variant: 'BODY_MD',
              class: [
                'group-data-[hide-search=false]:[transition:visibility_0s,opacity_200ms,background_200ms]',
                'group-data-[hide-search=true]:[transition:visibility_200ms,opacity_200ms,background_200ms]',
                'group-data-[hide-search=true]:invisible group-data-[hide-search=true]:opacity-0',
                'hidden header-md:block visible opacity-100 bg-token-alphalight-300 rounded-2xl border-0 text-350',
                'h-11 overflow-hidden text-ellipsis px-10 relative whitespace-nowrap z-10 backdrop-blur-[5px]',
                'w-full header-lg:max-w-[318px] header-md:max-w-[228px] hover:bg-token-alphalight-400',
                'focus-visible:outline-[3px] focus-visible:outline-token-gray-600 focus-visible:outline-solid',
              ],
            })}
            ref={buttonRef}
            aria-keyshortcuts="Meta+K Control+K /"
            onClick={() => ampli.searchFocused({ search_type: searchType })}
          >
            <span className="text-token-gray-800">Search tokens</span>
            <SearchIcon className="text-token-gray-1600 h-4 w-4 pointer-none absolute top-3.25 left-4 z-10" />
            <SearchShortcutKeycap className="flex absolute right-4 top-[9px] z-10" />
          </button>
        </SelectTokenDialog>

        <SelectTokenDialog
          onSelect={handleSelectTokenResult}
          searchType={isHomepage ? SEARCH_TYPES.HOME_PAGE : SEARCH_TYPES.HEADER}
        >
          <button
            className={buttons({
              size: 'medium',
              color: 'white',
              shape: 'pill',
              iconOnly: true,
              class: [
                'group-data-[hide-search=true]:hidden group-data-[hide-search=false]:block',
                'group-data-[hide-search=false]:header-md:hidden size-10 items-center justify-center p-3',
              ],
            })}
          >
            <SearchIcon className="size-4" />
            <VisuallyHidden>Search</VisuallyHidden>
          </button>
        </SelectTokenDialog>

        <FeatureFlagIndicator />
        <div data-backpack-confetti-anchor>
          <HeaderConnectButton />
        </div>

        <div className="relative">
          <AdditionalOptionsDropdown
            onOpen={() => setShowFeedbackHint(false)}
            showPulsingDot={showFeedbackHint}
            onTradeHistoryClick={handleHistoryClick}
            onPortfolioClick={handlePortfolioClick}
            tradePage={tradePage}
          />
          {showFeedbackHint && <FeedbackTooltip onDismiss={() => setShowFeedbackHint(false)} />}
        </div>
      </div>
    </HeaderBase>
  );
};
